import { useState } from "react";
import api, { WHATSAPP_NUMBER, CONTACT_EMAIL, WHATSAPP_LINK } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Mail, Phone, MessageCircle, MapPin } from "lucide-react";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast.error("Please fill name, email and phone.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/inquiries", form);
      toast.success("Message sent. We'll be in touch.");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch {
      toast.error("Could not send message.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page" className="bg-[#FCFCFC]">
      <section className="bg-white border-b border-gray-100">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16 py-16 md:py-20">
          <p className="overline mb-4">Get in touch</p>
          <h1 className="font-display text-5xl md:text-6xl tracking-tighter font-light max-w-3xl">
            We&apos;d love to speak.
          </h1>
        </div>
      </section>

      <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16 py-20 grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-5 space-y-8">
          <ContactItem icon={Phone} title="Call us" value={`+91 ${WHATSAPP_NUMBER}`} href={`tel:+91${WHATSAPP_NUMBER}`} testid="contact-phone" />
          <ContactItem icon={MessageCircle} title="WhatsApp" value="Chat with our concierge" href={WHATSAPP_LINK("Hello Royale Yaan")} testid="contact-whatsapp" />
          <ContactItem icon={Mail} title="Email" value={CONTACT_EMAIL} href={`mailto:${CONTACT_EMAIL}`} testid="contact-email" />
          <ContactItem icon={MapPin} title="Salon" value="By appointment · Mumbai · Delhi · Bengaluru" testid="contact-salon" />
        </div>

        <form onSubmit={submit} className="lg:col-span-7 bg-white border border-gray-200 p-8 md:p-10 space-y-5" data-testid="contact-form">
          <p className="overline mb-2">Send a message</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <Field id="cname" label="Full name *">
              <Input id="cname" data-testid="contact-name" value={form.name} onChange={(e)=>setForm({...form,name:e.target.value})} />
            </Field>
            <Field id="cphone" label="Phone *">
              <Input id="cphone" data-testid="contact-phone-field" value={form.phone} onChange={(e)=>setForm({...form,phone:e.target.value})} />
            </Field>
          </div>
          <Field id="cemail" label="Email *">
            <Input id="cemail" type="email" data-testid="contact-email-field" value={form.email} onChange={(e)=>setForm({...form,email:e.target.value})} />
          </Field>
          <Field id="cmsg" label="Message">
            <Textarea id="cmsg" rows={5} data-testid="contact-message" value={form.message} onChange={(e)=>setForm({...form,message:e.target.value})} />
          </Field>
          <div className="pt-2 flex justify-end">
            <Button type="submit" disabled={submitting} data-testid="contact-submit" className="btn-primary rounded-sm h-12 px-10">
              {submitting ? "Sending…" : "Send Message"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ id, label, children }) {
  return (
    <div>
      <Label htmlFor={id} className="text-xs uppercase tracking-[0.15em] text-gray-600">{label}</Label>
      <div className="mt-2">{children}</div>
    </div>
  );
}

function ContactItem({ icon: Icon, title, value, href, testid }) {
  const content = (
    <div className="flex items-start gap-4 group">
      <Icon size={20} strokeWidth={1.5} className="mt-1 text-black" />
      <div>
        <p className="overline mb-1">{title}</p>
        <p className="text-base text-gray-800 group-hover:text-black">{value}</p>
      </div>
    </div>
  );
  return href ? <a href={href} target={href.startsWith('http')?'_blank':undefined} rel="noreferrer" data-testid={testid}>{content}</a> : <div data-testid={testid}>{content}</div>;
}
